import type paper from "paper";
import type {
  Cutout,
  Decoration,
  Hole,
  Piece,
  Project,
  StitchLine,
} from "./Project";
import {
  nextCutoutName,
  nextPartName,
  nextStampDecorationName,
  nextStitchName,
  pieceForStitchLine,
  pieceHardware,
  pieceStitchLines,
} from "./Project";
import type { EditorContext } from "../tools/EditorContext";
import {
  translatePiece,
  translatePathData,
  translateDecoration,
  pieceDisplayOffset,
} from "./pieceTransform";
import { newId } from "./ids";

/**
 * Duplicate leather parts (#907). Mirrors the construction-line duplicate
 * (`duplicateConstructionSelection`): every selected part is deep-cloned with
 * fresh ids throughout, offset a small amount so the copy reads as distinct, and
 * the selection moves to the new copies so a follow-up drag / duplicate operates
 * on them.
 */

/**
 * Deep-clone a part for duplication. Fresh ids for the part AND every child
 * feature — holes, cutouts, stitch lines, decorations, hardware — so the copy
 * shares nothing mutable with its source. Hardware is re-pointed at the cloned
 * holes (it references a `Hole.id` on the same part). Cross-references that would
 * secretly tie the copy to its original are dropped, matching how
 * `duplicateConstructionSelection` refuses to carry constraints across the copy
 * boundary:
 *   - `constructionOutline` on the part, its cutouts and its stitch lines — a
 *     LINKED copy would re-derive right back onto the same construction geometry
 *     (landing on top of the original), so we bake to an independent frozen
 *     snapshot of the CURRENT geometry instead (see the linked-part note in
 *     `duplicatePieceSelection`).
 *   - `matchId` on stitch lines — a duplicate is independent, so it starts life
 *     un-matched; the commit pipeline re-derives matches from scratch.
 */
export function clonePieceForDuplicate(piece: Piece, name: string): Piece {
  // Remap hole ids first so hardware on the copy can point at the cloned holes.
  const holeIdMap = new Map<string, string>();
  const holes = piece.holes.map((h) => {
    const id = newId("hole");
    holeIdMap.set(h.id, id);
    return { ...h, id };
  });
  const cutouts = piece.cutouts.map((c) => {
    const { constructionOutline: _drop, ...rest } = c;
    return { ...rest, id: newId("cut") };
  });
  const stitchLines = pieceStitchLines(piece).map((s) => {
    const { constructionOutline: _co, matchId: _match, ...rest } = s;
    return { ...rest, id: newId("stitch") } as StitchLine;
  });
  const decorations = piece.decorations.map((d) => ({
    ...d,
    id: newId("dec"),
  }));
  const hardware = pieceHardware(piece).map((hw) => ({
    ...hw,
    id: newId("hw"),
    holeId: holeIdMap.get(hw.holeId) ?? hw.holeId,
  }));
  const { constructionOutline: _link, ...rest } = piece;
  return {
    ...rest,
    id: newId("piece"),
    name,
    holes,
    cutouts,
    stitchLines,
    decorations,
    hardware,
  };
}

/**
 * Selection-driven part duplicate. Clones every selected part, generating fresh
 * ids and offsetting the copies by one minor grid step along both axes (the same
 * nudge the construction duplicate uses, so a single Up-then-Left arrow-tap walks
 * a copy back onto its original). Each copy carries independent deep copies of
 * its child features. After the commit the new copies become the selection.
 * Returns the new part ids, or null when no part was selected.
 *
 * LINKED PARTS: a part traced from construction geometry (`constructionOutline`)
 * is duplicated as an UNLINKED, frozen copy of its CURRENT displayed geometry —
 * `clonePieceForDuplicate` drops the outline link and the copy is translated to
 * where the original is drawn (its cut-layout `pieceDisplayOffset`) plus the
 * nudge. A linked copy would re-derive from the SAME construction lines and snap
 * right back on top of the original, which is the opposite of what "duplicate"
 * should do; freezing keeps the copy visibly beside its source and editable on
 * its own.
 */
export function duplicatePieceSelection(
  ctx: EditorContext,
  opts: { offsetMm?: number } = {},
): string[] | null {
  const sel = ctx.scene.getSelection();
  const ids = sel.pieceIds ?? (sel.pieceId ? [sel.pieceId] : []);
  if (ids.length === 0) return null;

  const offsetMm = opts.offsetMm ?? ctx.scene.nudgeStep("minor");
  const scope = ctx.scene.getPaperScope();
  const view = ctx.scene.getPartLayoutView();
  const idSet = new Set(ids);

  const newIds: string[] = [];
  ctx.commit("Duplicate part", (proj) => {
    let next = proj;
    // Walk in project-array order so multi-select copies keep a stable,
    // predictable draw order regardless of the click order.
    for (const src of proj.pieces) {
      if (!idSet.has(src.id)) continue;
      // Where the original is DISPLAYED vs. where its geometry lives: a linked
      // part's cut-layout offset (`constructionOutline.layout`) renders in
      // Spread but isn't baked into the geometry. The clone strips the link, so
      // translate it to the displayed position first, then add the nudge.
      const off = pieceDisplayOffset(src, view);
      // Fresh auto-numbered name in the "Part N" family (#907) — the SAME
      // next-available-number helper new parts use, so a duplicate reads as a
      // brand-new part rather than inheriting the original's name. `next`
      // already holds copies minted earlier in this loop, so their numbers are
      // counted and each copy lands on its own free number.
      const name = nextPartName(next);
      const clone = translatePiece(
        clonePieceForDuplicate(src, name),
        off.dx + offsetMm,
        off.dy + offsetMm,
        scope,
      );
      newIds.push(clone.id);
      next = { ...next, pieces: [...next.pieces, clone] };
    }
    return next;
  });

  if (newIds.length === 0) return null;
  ctx.setSelection({
    pieceId: newIds[newIds.length - 1] ?? null,
    pieceIds: newIds,
    decorationId: null,
    holeId: null,
    cutoutId: null,
    stitchId: null,
    // Duplicating parts clears any construction-side selection so the two
    // scopes don't co-exist and confuse the panel / shortcuts.
    constructionIds: [],
    constructionGroupIds: [],
    tracingImageIds: [],
  });
  return newIds;
}

// --- Child-feature duplicate (#907 follow-up) -------------------------------
//
// When a single CHILD of a part is selected — a stitch line, cutout, hole, or
// decoration — Duplicate clones just THAT feature into its OWN parent piece,
// rather than the whole part. The copy gets a fresh id, is offset by the same
// minor-grid nudge (so an Up-then-Left arrow-tap walks it back onto the
// original), appends to the owner's feature array, and becomes the selection.
//
// The nudge is applied in the part's ORIGINAL (model) coordinate space (#786).
// The copy shares its owner's cut-layout render translate with the original, so
// a same-space nudge shows up as exactly that nudge between the two in the
// displayed (spread / moved-part) view — no display-space correction needed.
//
// Hardware isn't independently selectable (it isn't wired through the scene
// selection), so it has no standalone path; instead, duplicating a HOLE also
// clones any hardware seated on it, re-pointed at the new hole.
//
// NAMING (#907): each copy gets a fresh, auto-numbered name in its own family —
// the SAME next-available-number helper new objects use (`nextStitchName` /
// `nextCutoutName`, and a base-derived `nextStampDecorationName` for
// decorations) — so a copy never shares the original's label. Holes carry no
// `name` field (the layers panel labels them positionally, "Hole N", by index),
// so an appended copy simply becomes the next index — nothing to set.

/** Fresh name for a duplicated decoration: continue the original's own family
 *  (its name minus any trailing " N") with the next free number, e.g.
 *  "Basket Weave 2" → "Basket Weave 3", "Border" → "Border 1". Reuses
 *  `nextStampDecorationName`, the same next-free-in-family helper the stamp /
 *  fill / line-stamp tools use when creating decorations. */
export function nextDuplicateDecorationName(
  piece: Piece,
  originalName: string,
): string {
  const base = originalName.replace(/\s+\d+$/, "").trim() || originalName;
  return nextStampDecorationName(piece, base);
}

/** Map one piece by id, leaving the rest untouched. */
export function mapPiece(
  proj: Project,
  pieceId: string,
  fn: (piece: Piece) => Piece,
): Project {
  return {
    ...proj,
    pieces: proj.pieces.map((p) => (p.id === pieceId ? fn(p) : p)),
  };
}

/** Clone a stitch line for in-part duplication: fresh id, fresh "Stitch N" name,
 *  geometry nudged, and the independence drops of a copy — `constructionOutline`
 *  (a linked copy would re-derive back onto its source) and `matchId` (a copy
 *  starts un-matched; the commit pipeline re-derives matches). */
export function cloneStitchForDuplicate(
  s: StitchLine,
  dx: number,
  dy: number,
  name: string,
): StitchLine {
  const { constructionOutline: _co, matchId: _match, ...rest } = s;
  return {
    ...rest,
    id: newId("stitch"),
    name,
    points: rest.points.map((pt) => ({ x: pt.x + dx, y: pt.y + dy })),
    authoredPoints: rest.authoredPoints?.map((pt) => ({
      x: pt.x + dx,
      y: pt.y + dy,
    })),
  };
}

/** Clone a cutout for in-part duplication: fresh id, fresh "Cutout N" name, path
 *  nudged, and the construction link dropped (frozen independent copy, as for
 *  stitches). */
export function cloneCutoutForDuplicate(
  c: Cutout,
  dx: number,
  dy: number,
  scope: paper.PaperScope,
  name: string,
): Cutout {
  const { constructionOutline: _co, ...rest } = c;
  return {
    ...rest,
    id: newId("cut"),
    name,
    pathData: translatePathData(rest.pathData, dx, dy, scope),
  };
}

/** Nudge a hole to a fresh id + offset position. */
export function cloneHoleForDuplicate(h: Hole, dx: number, dy: number): Hole {
  return {
    ...h,
    id: newId("hole"),
    position: { x: h.position.x + dx, y: h.position.y + dy },
  };
}

/**
 * Duplicate the selected CHILD feature(s) of a part into their owner piece.
 * Returns true when the selection was a child-feature scope (handled — even if
 * the specific ids couldn't be resolved), false when it wasn't (so the caller
 * falls through to the whole-piece / construction paths). Child scope is
 * detected BEFORE piece scope because a child selection also carries its owner's
 * `pieceId`.
 */
export function duplicateChildFeatureSelection(ctx: EditorContext): boolean {
  const sel = ctx.scene.getSelection();
  const offset = ctx.scene.nudgeStep("minor");
  const scope = ctx.scene.getPaperScope();

  // Stitch lines — single or a multi-set that can span pieces (#199 matching),
  // so resolve each stitch's own owner rather than assuming `sel.pieceId`.
  const stitchIds = sel.stitchIds ?? (sel.stitchId ? [sel.stitchId] : []);
  if (stitchIds.length > 0) {
    const cloned: Array<{ ownerId: string; id: string }> = [];
    ctx.commit("Duplicate stitch line", (proj) => {
      let next = proj;
      for (const sid of stitchIds) {
        const owner = pieceForStitchLine(next, sid);
        const src = owner
          ? pieceStitchLines(owner).find((s) => s.id === sid)
          : null;
        if (!owner || !src) continue;
        // Fresh "Stitch N" — numbered globally across parts, counting copies
        // already appended to `next` so a multi-select run stays collision-free.
        const clone = cloneStitchForDuplicate(
          src,
          offset,
          offset,
          nextStitchName(next),
        );
        cloned.push({ ownerId: owner.id, id: clone.id });
        next = mapPiece(next, owner.id, (pc) => ({
          ...pc,
          stitchLines: [...pieceStitchLines(pc), clone],
        }));
      }
      return next;
    });
    if (cloned.length > 0) {
      const newIds = cloned.map((c) => c.id);
      ctx.setSelection({
        pieceId: cloned[cloned.length - 1]!.ownerId,
        decorationId: null,
        holeId: null,
        cutoutId: null,
        stitchId: newIds[newIds.length - 1] ?? null,
        stitchIds: newIds,
      });
    }
    return true;
  }

  const pieceId = sel.pieceId;
  if (!pieceId) return false;

  // Cutout.
  if (sel.cutoutId) {
    const cutoutId = sel.cutoutId;
    let newCutoutId: string | null = null;
    ctx.commit("Duplicate cutout", (proj) => {
      const owner = proj.pieces.find((p) => p.id === pieceId);
      const src = owner?.cutouts.find((c) => c.id === cutoutId);
      if (!owner || !src) return proj;
      // Fresh "Cutout N" name in the owner part, not the original's.
      const clone = cloneCutoutForDuplicate(
        src,
        offset,
        offset,
        scope,
        nextCutoutName(owner),
      );
      newCutoutId = clone.id;
      return mapPiece(proj, pieceId, (pc) => ({
        ...pc,
        cutouts: [...pc.cutouts, clone],
      }));
    });
    if (newCutoutId) {
      ctx.setSelection({
        pieceId,
        decorationId: null,
        holeId: null,
        stitchId: null,
        cutoutId: newCutoutId,
      });
    }
    return true;
  }

  // Hole (+ any hardware seated on it — hardware has no standalone selection).
  if (sel.holeId) {
    const holeId = sel.holeId;
    let newHoleId: string | null = null;
    ctx.commit("Duplicate hole", (proj) => {
      const owner = proj.pieces.find((p) => p.id === pieceId);
      const src = owner?.holes.find((h) => h.id === holeId);
      if (!owner || !src) return proj;
      const clone = cloneHoleForDuplicate(src, offset, offset);
      newHoleId = clone.id;
      const seated = pieceHardware(owner).filter((hw) => hw.holeId === holeId);
      const clonedHw = seated.map((hw) => ({
        ...hw,
        id: newId("hw"),
        holeId: clone.id,
      }));
      return mapPiece(proj, pieceId, (pc) => ({
        ...pc,
        holes: [...pc.holes, clone],
        hardware:
          clonedHw.length > 0
            ? [...pieceHardware(pc), ...clonedHw]
            : pc.hardware,
      }));
    });
    if (newHoleId) {
      ctx.setSelection({
        pieceId,
        decorationId: null,
        cutoutId: null,
        stitchId: null,
        holeId: newHoleId,
      });
    }
    return true;
  }

  // Decoration.
  if (sel.decorationId) {
    const decorationId = sel.decorationId;
    let newDecorationId: string | null = null;
    ctx.commit("Duplicate decoration", (proj) => {
      const owner = proj.pieces.find((p) => p.id === pieceId);
      const src = owner?.decorations.find((d) => d.id === decorationId);
      if (!owner || !src) return proj;
      const moved: Decoration = translateDecoration(src, offset, offset, scope);
      // Fresh name in the decoration's own family, not a duplicate label.
      const clone: Decoration = {
        ...moved,
        id: newId("dec"),
        name: nextDuplicateDecorationName(owner, src.name),
      };
      newDecorationId = clone.id;
      return mapPiece(proj, pieceId, (pc) => ({
        ...pc,
        decorations: [...pc.decorations, clone],
      }));
    });
    if (newDecorationId) {
      ctx.setSelection({
        pieceId,
        holeId: null,
        cutoutId: null,
        stitchId: null,
        decorationId: newDecorationId,
      });
    }
    return true;
  }

  return false;
}
